import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { logInDev } from '@ocx/utils';

type RefetchFn = () => Promise<unknown>;

interface HomeRefreshContextValue {
  register: (fn: RefetchFn) => () => void;
  refreshAll: () => Promise<void>;
  /** True while any home section is fetching data (cold load, background revalidation under
   *  `cache-and-network`, or pull-to-refresh — the indicator and the pull-to-refresh visual
   *  may show simultaneously, that's accepted). */
  refreshing: boolean;
  setRefreshing: (isRefreshing: boolean) => void;
}

const HomeRefreshContext = createContext<HomeRefreshContextValue | null>(null);

type HomeRefreshProviderProps = {
  extraRefreshFns?: RefetchFn[];
  onBeforeRefresh?: () => void;
  children: ReactNode;
};

export const HomeRefreshProvider = ({ children, extraRefreshFns = [], onBeforeRefresh }: HomeRefreshProviderProps) => {
  const refetchFns = useRef(new Set<RefetchFn>());
  const [refreshing, setRefreshing] = useState(false);
  const extraRef = useRef(extraRefreshFns);
  extraRef.current = extraRefreshFns;

  const onBeforeRefreshRef = useRef(onBeforeRefresh);
  onBeforeRefreshRef.current = onBeforeRefresh;

  const register = useCallback((fn: RefetchFn) => {
    refetchFns.current.add(fn);
    return () => {
      refetchFns.current.delete(fn);
    };
  }, []);

  const refreshAll = useCallback(async () => {
    onBeforeRefreshRef.current?.();
    const safeCall = (fn: RefetchFn) => Promise.resolve().then(fn);
    const results = await Promise.allSettled([
      ...Array.from(refetchFns.current).map(safeCall),
      ...extraRef.current.map(safeCall),
    ]);
    results.forEach((result) => {
      if (result.status === 'rejected') {
        logInDev(result.reason);
      }
    });
  }, []);

  const value = useMemo(
    () => ({ register, refreshAll, refreshing, setRefreshing }),
    [refreshAll, register, refreshing],
  );

  return <HomeRefreshContext.Provider value={value}>{children}</HomeRefreshContext.Provider>;
};

export const useHomeRefresh = () => {
  const ctx = useContext(HomeRefreshContext);
  if (!ctx) {
    throw new Error('useHomeRefresh must be used within HomeRefreshProvider');
  }
  return ctx;
};

export const useRegisterRefetch = (refetchFn: RefetchFn) => {
  const { register } = useHomeRefresh();
  useEffect(() => {
    return register(refetchFn);
  }, [refetchFn, register]);
};

/**
 * Report this section's data-fetching state to the home page so the shared progress
 * indicator can reflect it. Only one section should set this at a time — currently only
 * one of the home offers controllers is rendered (`gridLocation === Tab` vs `Home`).
 */
export const useHomeRefreshing = (isRefreshing: boolean) => {
  const { setRefreshing } = useHomeRefresh();
  useEffect(() => {
    setRefreshing(isRefreshing);
    return () => setRefreshing(false);
  }, [isRefreshing, setRefreshing]);
};
